import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      contact: 'Contact',
      bookCall: 'Book a Call',
      freeEbook: 'Free Ebook',
      aiStemKidz: 'AI STEM Kidz'
    },
    // Hero Section
    hero: {
      headline: 'Your AI Ecosystem for Growth.',
      subheadline: 'Launch Smarter, Not Harder.',
      description: 'DMTAI Strategies builds your integrated digital brain—where AI agents, applications, and automation work in harmony to scale your vision.',
      cta: 'Architect My AI Ecosystem'
    },
    // Problem/Solution
    problem: {
      headline: 'Fragmented Tools. Wasted Time. Missed Opportunities.',
      description: 'Most AI solutions are point-based—a chatbot here, an analytics tool there. True power comes from integration. We design cohesive strategies where every piece communicates, automating workflows and delivering insights that drive real growth.'
    },
    // Services
    services: {
      headline: 'Our Integrated Service Ecosystem',
      cta: 'Dive into the AI Ecosystem',
      items: {
        aiAgents: {
          title: 'AI Agents & Automation',
          description: 'Deploy intelligent agents for customer support, lead qualification, and internal operations.'
        },
        customApps: {
          title: 'Custom AI Applications',
          description: 'Medical-grade, reliable AI tools for specific verticals (healthcare, finance, SaaS) and business challenges.'
        },
        webDev: {
          title: 'Web Development & Strategy',
          description: 'Future-proof websites that are the central hub of your AI ecosystem, designed for conversion and user experience.'
        },
        seo: {
          title: 'SEO & Intelligent Content',
          description: 'Data-driven SEO strategies amplified by AI to ensure your ecosystem attracts the right audience.'
        },
        consulting: {
          title: 'AI Business Consulting',
          description: 'Strategic roadmap to integrate AI into your business model, processes, and customer journey.'
        }
      }
    },
    // Audience
    audience: {
      headline: 'Designed for Visionaries Who Execute',
      description: "You're the expert in your field. We're the experts in building your AI-powered advantage.",
      personas: {
        entrepreneur: 'Strategic Entrepreneurs',
        professional: 'Physicians & Healthcare Pros',
        business: 'SMB Owners',
        investor: 'Investors & Consultants'
      }
    },
    // CTA Section
    cta: {
      headline: 'Build Your Integrated Advantage',
      consultation: {
        title: 'Strategic Consultation',
        description: 'Book a 1-on-1 session to map your AI future.',
        button: 'Book a Call'
      },
      demo: {
        title: 'Know the Ecosystem',
        description: 'Request a live demo of our AI agents and applications in action.',
        button: 'Schedule a Demo'
      },
      services: {
        title: 'Deploy a Solution',
        description: 'Ready to implement? Explore our flagship AI product packages.',
        button: 'View Services'
      }
    },
    // Footer
    footer: {
      tagline: 'The AI Integration Agency',
      slogan: 'Building integrated AI ecosystems for businesses ready to scale.',
      quickLinks: 'Quick Links',
      audit: 'Get a Free Strategy Audit',
      contact: 'Contact Us',
      freeEbook: 'Free Ebook',
      ebookTitle: 'Complete Guide to Designing Attractive Websites',
      ebookCta: 'Download Free'
    },
    // Services Page
    servicesPage: {
      headline: 'Complete Digital Solutions',
      subheadline: 'From strategy to execution, we power your digital transformation',
      backToHome: 'Back to Home',
      startingFrom: 'Starting from',
      contactUs: 'Contact Us',
      readyToTransform: 'Ready to Transform Your Business?',
      readyToTransformDesc: 'Let\'s discuss how our integrated AI ecosystem can help you launch faster, grow smarter, and operate efficiently.',
      bookConsultation: 'Book a Consultation',
      chatWhatsApp: 'Chat on WhatsApp',
      categories: {
        marketing: {
          name: 'Digital Marketing',
          description: 'Comprehensive digital marketing strategies to boost your online presence and drive conversions.',
          price: '$500'
        },
        socialMedia: {
          name: 'Social Media Administration',
          description: 'Full social media management including content creation, scheduling, and community engagement.',
          price: '$400'
        },
        ads: {
          name: 'ADS Management',
          description: 'Google, Facebook, Instagram & LinkedIn ads optimization for maximum ROI.',
          price: '$600'
        },
        mobileApps: {
          name: 'Mobile Apps',
          description: 'Custom iOS and Android app development tailored to your business needs.',
          price: '$3,000'
        },
        consulting: {
          name: 'Business Consulting',
          description: 'Strategic business consulting to optimize operations and accelerate growth.',
          price: '$200/hr'
        },
        branding: {
          name: 'Branding Design',
          description: 'Complete brand identity design including logo, colors, and brand guidelines.',
          price: '$800'
        },
        webDesign: {
          name: 'Web Design',
          description: 'Modern, responsive website design that converts visitors into customers.',
          price: '$1,500'
        },
        ecommerce: {
          name: 'E-commerce',
          description: 'Full e-commerce solutions with payment integration and inventory management.',
          price: '$2,500'
        },
        emailMarketing: {
          name: 'Email Marketing',
          description: 'Automated email campaigns that nurture leads and boost customer retention.',
          price: '$350'
        },
        seo: {
          name: 'SEO Strategies',
          description: 'Data-driven SEO optimization to improve search rankings and organic traffic.',
          price: '$450'
        },
        payments: {
          name: 'Online Payment Systems',
          description: 'Secure payment gateway integration for seamless online transactions.',
          price: '$500'
        },
        hosting: {
          name: 'Hosting',
          description: 'Reliable, high-performance hosting solutions with 99.9% uptime guarantee.',
          price: '$50/mo'
        },
        ai: {
          name: 'AI Technologies',
          description: 'Custom AI solutions including chatbots, automation, and machine learning integration.',
          price: '$2,000'
        },
        socialMediaMgmt: {
          name: 'Social Media',
          description: 'Strategic social media presence management across all major platforms.',
          price: '$400'
        },
        ar: {
          name: 'Augmented Reality',
          description: 'Immersive AR experiences for marketing, training, and customer engagement.',
          price: '$5,000'
        },
        projectMgmt: {
          name: 'Project Management',
          description: 'End-to-end project management ensuring timely delivery and quality results.',
          price: '$150/hr'
        }
      }
    },
    // Chat
    chat: {
      title: 'DMTAI Assistant',
      placeholder: 'Ask me about our AI services...',
      welcome: 'Hello! I\'m your DMTAI AI assistant. How can I help you today?',
      send: 'Send'
    },
    // Forms
    forms: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      message: 'Message',
      submit: 'Submit',
      submitting: 'Submitting...',
      success: 'Thank you! We\'ll be in touch soon.',
      error: 'Something went wrong. Please try again.'
    },
    // AI STEM Kidz Page
    aiStemKidz: {
      hero: {
        tag: 'AI Literacy Program · Ages 8–12',
        headlinePart1: 'Turn your child from a tech consumer into an',
        headlinePart2: 'AI creator.',
        subhead: 'AI STEM Kidz — 6-month project-based program for ages 8–12. Build a professional portfolio with images, videos, websites, and apps.',
        cta: 'Join Founding Families',
        trust: '20+ years tech expertise · Human-centric AI framework',
        spotsLeft: 'spots remaining of 25',
        foundingNote: 'First 10 families get lifetime founding pricing.',
        playLabel: 'Click to play intro',
        videoCaption: 'A personal intro from Dario, your AI literacy guide.'
      },
      problem: {
        title: 'The Problem',
        list: [
          'Kids passively consume tech (scrolling, gaming)',
          'Parents lack guidance on safe AI use',
          'Schools lag behind in AI literacy',
          'Traditional STEM feels abstract to kids'
        ]
      },
      solution: {
        title: 'Our Solution',
        list: [
          'Project-based creation with 12+ industry AI tools',
          'Human-centric AI + weekly "Co-Pilot Challenges" for parents',
          'Age-appropriate curriculum designed for 8–12',
          'Tangible portfolio artifacts + monthly certifications',
          'Weekly tutoring FAQ session'
        ]
      },
      valueProp: {
        quote: 'Transform your 8–12-year-old from tech consumer to AI creator in 6 months — building a professional portfolio through project-based learning with parental co-pilot support.',
        highlights: ['6 months', '12+ pro AI tools', 'Monthly portfolio pieces', 'Parent co-pilot'],
        spanishNote: 'The first and unique AI STEM Kidz course in Spanish for kids. Bilingual learners lead the AI world.'
      },
      how: {
        title: 'How It Works',
        subtitle: 'Four steps from curious kid to certified AI creator.',
        s1: { title: 'Enrol in 6-month cohort', desc: 'Founding families get a bonus setup guide.' },
        s2: { title: 'Weekly project missions', desc: 'Videos, websites, AI art, simple apps.' },
        s3: { title: 'Parent co-pilot challenges', desc: '30-min activities to reinforce safety & creativity.' },
        s4: { title: 'Earn badges + final cert', desc: 'AI STEM Kidz Certified Creator after 6 months.' },
        cta: 'Claim your spot now'
      },
      curriculum: {
        title: 'Real AI Tools — Same Ones the Pros Use ·',
        subtitle: 'Your child learns by creating with industry-grade AI, in a kid-safe environment.',
        sampleTitle: 'Sample Projects',
        projects: [
          'Build an AI comic strip',
          'Create a talking avatar',
          'Code a chatbot',
          'Design a movie poster'
        ]
      },
      parental: {
        tag: 'Parents Included',
        title: "You're not replaced — you're empowered.",
        subtitle: 'Every week, get a short co-pilot challenge that turns AI learning into family bonding.',
        features: [
          'Weekly co-pilot challenges (5–10 min for parents)',
          'Human-centric AI framework: ethics, bias, fact-checking',
          'No experience needed — setup guides & email support included',
          'Screen time reimagined: creative production, not passive scrolling'
        ]
      },
      cert: {
        title: 'Certification & Portfolio',
        subtitle: 'Every month unlocks a digital badge. After 6 months: a full portfolio + Master Creator certificate.',
        b1: 'AI Explorer',
        b2: 'Prompt Master',
        b3: 'Ethical AI Agent',
        b4: 'AI Storyteller',
        b5: 'Bot Builder',
        b6: 'AI Innovator',
        portfolio: [
          { title: 'AI Comic Strip', desc: 'Original character art + dialogue with kid-safe AI.' },
          { title: 'Talking Avatar Video', desc: 'Voice cloning + animation, narrated by the student.' },
          { title: 'Simple Chatbot Site', desc: 'A working website + chatbot deployed by the student.' }
        ],
        footnote: 'Every month = 1 portfolio piece. After 6 months: full digital portfolio for school or showcases.'
      },
      pricing: {
        title: 'Founding Family Pricing',
        subtitle: 'Two ways to enrol. Both include certification, all tools, and the parent handbook.',
        bestValue: 'Best Value',
        p1Title: '6-Month Program',
        p1Note: 'One-time payment',
        p1Features: [
          'All 6 modules · 12+ AI tools',
          'Monthly digital badges',
          'Final Master Creator certificate',
          'Parent handbook + co-pilot guides',
          'Weekly FAQ tutoring session'
        ],
        p2Title: 'Monthly Subscription',
        p2Note: '+ 10% initial inscription · 10% interest on late payments',
        month: 'month',
        p2Features: [
          'All 6 modules · 12+ AI tools',
          'Monthly digital badges',
          'Final Master Creator certificate',
          'Parent handbook + co-pilot guides',
          'Weekly FAQ tutoring session'
        ],
        cta: 'Claim Founding Spot',
        bonusTitle: 'Founding Families Bonus',
        bonusDesc: 'FREE inscription + free handbook',
        value: 'value'
      },
      audience: {
        title: 'Built for these families',
        a1: 'Tech-forward parents (35–50)',
        a2: 'Homeschool families',
        a3: 'Private school parents',
        a4: 'Gifted learners (8–12)',
        a5: 'Safety-conscious parents'
      },
      social: {
        title: 'Social Proof & Viral Loop',
        loops: [
          'Every student showcase shared by parents → peer inquiries',
          'Co-pilot challenges turn parents into advocates',
          'Digital badges = social proof on LinkedIn & school resumes'
        ],
        testimonial: 'Finally, an AI program that includes me as a parent.',
        testimonialBy: 'Future Founding Parent'
      },
      risk: {
        title: 'Common questions, answered',
        items: [
          { q: 'Is AI safe for my child?', a: 'Yes. We use a human-centric AI framework with kid-safe tools, parental co-pilot, and a dedicated safety module.' },
          { q: 'Is my child too young/old (age range)?', a: 'The curriculum is built for ages 8–12, with sample projects and a free starter kit available by email.' },
          { q: 'I have no tech experience — can I support my child?', a: 'No experience needed. We guide both child and parent with setup guides and email support.' },
          { q: 'Will this just add more screen time?', a: 'No. We emphasise creative production over passive consumption — the screen becomes a tool, not a trap.' },
          { q: 'Is the price worth it?', a: 'Compare to tutoring: $11,999 MXN for 6 months ≈ a tiny weekly investment for portfolio + certification + lifelong AI fluency.' }
        ]
      },
      closing: {
        title1: 'Is your kid consuming or',
        title2: 'creating content?',   
        enrol: 'Enrol Now',
        book: 'Book a 1-1 Consultation',
        disclaimer: 'AI literacy focus, not a replacement for school. Limited spots · founding pricing locked for life.',
        founder: 'Founder: Recognised tech expert, 20+ years.'
      },
      floating: 'Enrol Now',
      floatingShort: 'Enrol'
    }
  },
  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      contact: 'Contacto',
      bookCall: 'Reservar Llamada',
      freeEbook: 'Ebook Gratis',
      aiStemKidz: 'AI STEM Kidz'
    },
    // Hero Section
    hero: {
      headline: 'Tu Ecosistema de IA para Crecer.',
      subheadline: 'Lanza Inteligente, No Difícil.',
      description: 'DMTAI Strategies construye tu cerebro digital integrado—donde agentes de IA, aplicaciones y automatización trabajan en armonía para escalar tu visión.',
      cta: 'Arquitecta Mi Ecosistema de IA'
    },
    // Problem/Solution
    problem: {
      headline: 'Herramientas Fragmentadas. Tiempo Perdido. Oportunidades Perdidas.',
      description: 'La mayoría de las soluciones de IA son puntuales—un chatbot aquí, una herramienta de análisis allá. El verdadero poder viene de la integración. Diseñamos estrategias cohesivas donde cada pieza se comunica, automatizando flujos de trabajo y entregando insights que impulsan el crecimiento real.'
    },
    // Services
    services: {
      headline: 'Nuestro Ecosistema de Servicios Integrados',
      cta: 'Explora el Ecosistema de IA',
      items: {
        aiAgents: {
          title: 'Agentes de IA y Automatización',
          description: 'Despliega agentes inteligentes para soporte al cliente, calificación de leads y operaciones internas.'
        },
        customApps: {
          title: 'Aplicaciones de IA Personalizadas',
          description: 'Herramientas de IA confiables de grado médico para verticales específicos (salud, finanzas, SaaS) y desafíos empresariales.'
        },
        webDev: {
          title: 'Desarrollo Web y Estrategia',
          description: 'Sitios web a prueba de futuro que son el centro de tu ecosistema de IA, diseñados para conversión y experiencia de usuario.'
        },
        seo: {
          title: 'SEO y Contenido Inteligente',
          description: 'Estrategias de SEO basadas en datos amplificadas por IA para asegurar que tu ecosistema atraiga la audiencia correcta.'
        },
        consulting: {
          title: 'Consultoría de Negocios con IA',
          description: 'Hoja de ruta estratégica para integrar IA en tu modelo de negocio, procesos y journey del cliente.'
        }
      }
    },
    // Audience
    audience: {
      headline: 'Diseñado para Visionarios que Ejecutan',
      description: 'Eres el experto en tu campo. Nosotros somos los expertos en construir tu ventaja potenciada por IA.',
      personas: {
        entrepreneur: 'Emprendedores Estratégicos',
        professional: 'Médicos y Profesionales de Salud',
        business: 'Dueños de Pymes',
        investor: 'Inversionistas y Consultores'
      }
    },
    // CTA Section
    cta: {
      headline: 'Construye Tu Ventaja Integrada',
      consultation: {
        title: 'Consulta Estratégica',
        description: 'Reserva una sesión 1 a 1 para mapear tu futuro con IA.',
        button: 'Reservar Llamada'
      },
      demo: {
        title: 'Conoce el Ecosistema',
        description: 'Solicita una demo en vivo de nuestros agentes y aplicaciones de IA en acción.',
        button: 'Agendar Demo'
      },
      services: {
        title: 'Implementa una Solución',
        description: '¿Listo para implementar? Explora nuestros paquetes de productos de IA.',
        button: 'Ver Servicios'
      }
    },
    // Footer
    footer: {
      tagline: 'La Agencia de Integración de IA',
      slogan: 'Construyendo ecosistemas de IA integrados para negocios listos para escalar.',
      quickLinks: 'Enlaces Rápidos',
      audit: 'Obtén una Auditoría de Estrategia Gratis',
      contact: 'Contáctanos',
      freeEbook: 'Ebook Gratis',
      ebookTitle: 'Guía Completa para Diseñar Sitios Web Atractivos',
      ebookCta: 'Descargar Gratis'
    },
    // Services Page
    servicesPage: {
      headline: 'Soluciones Digitales Completas',
      subheadline: 'De la estrategia a la ejecución, potenciamos tu transformación digital',
      backToHome: 'Volver al Inicio',
      startingFrom: 'Desde',
      contactUs: 'Contáctanos',
      readyToTransform: '¿Listo para Transformar tu Negocio?',
      readyToTransformDesc: 'Hablemos sobre cómo nuestro ecosistema de IA integrado puede ayudarte a lanzar más rápido, crecer más inteligente y operar eficientemente.',
      bookConsultation: 'Reservar Consulta',
      chatWhatsApp: 'Chatear en WhatsApp',
      categories: {
        marketing: {
          name: 'Marketing Digital',
          description: 'Estrategias de marketing digital integrales para impulsar tu presencia online y generar conversiones.',
          price: '$500'
        },
        socialMedia: {
          name: 'Administración de Redes Sociales',
          description: 'Gestión completa de redes sociales incluyendo creación de contenido, programación e interacción con la comunidad.',
          price: '$400'
        },
        ads: {
          name: 'Gestión de Anuncios',
          description: 'Optimización de anuncios en Google, Facebook, Instagram y LinkedIn para máximo ROI.',
          price: '$600'
        },
        mobileApps: {
          name: 'Aplicaciones Móviles',
          description: 'Desarrollo de apps iOS y Android personalizadas según las necesidades de tu negocio.',
          price: '$3,000'
        },
        consulting: {
          name: 'Consultoría de Negocios',
          description: 'Consultoría estratégica de negocios para optimizar operaciones y acelerar el crecimiento.',
          price: '$200/hr'
        },
        branding: {
          name: 'Diseño de Marca',
          description: 'Diseño completo de identidad de marca incluyendo logo, colores y guías de marca.',
          price: '$800'
        },
        webDesign: {
          name: 'Diseño Web',
          description: 'Diseño web moderno y responsivo que convierte visitantes en clientes.',
          price: '$1,500'
        },
        ecommerce: {
          name: 'Comercio Electrónico',
          description: 'Soluciones completas de e-commerce con integración de pagos y gestión de inventario.',
          price: '$2,500'
        },
        emailMarketing: {
          name: 'Email Marketing',
          description: 'Campañas de email automatizadas que nutren leads y mejoran la retención de clientes.',
          price: '$350'
        },
        seo: {
          name: 'Estrategias SEO',
          description: 'Optimización SEO basada en datos para mejorar rankings y tráfico orgánico.',
          price: '$450'
        },
        payments: {
          name: 'Sistemas de Pago Online',
          description: 'Integración segura de pasarelas de pago para transacciones online sin problemas.',
          price: '$500'
        },
        hosting: {
          name: 'Hospedaje',
          description: 'Soluciones de hosting confiables y de alto rendimiento con 99.9% de uptime garantizado.',
          price: '$50/mes'
        },
        ai: {
          name: 'Tecnologías de IA',
          description: 'Soluciones de IA personalizadas incluyendo chatbots, automatización e integración de machine learning.',
          price: '$2,000'
        },
        socialMediaMgmt: {
          name: 'Redes Sociales',
          description: 'Gestión estratégica de presencia en redes sociales en todas las plataformas principales.',
          price: '$400'
        },
        ar: {
          name: 'Realidad Aumentada',
          description: 'Experiencias AR inmersivas para marketing, capacitación e interacción con clientes.',
          price: '$5,000'
        },
        projectMgmt: {
          name: 'Gestión de Proyectos',
          description: 'Gestión de proyectos de principio a fin asegurando entrega oportuna y resultados de calidad.',
          price: '$150/hr'
        }
      }
    },
    // Chat
    chat: {
      title: 'Asistente DMTAI',
      placeholder: 'Pregúntame sobre nuestros servicios de IA...',
      welcome: '¡Hola! Soy tu asistente de IA de DMTAI. ¿Cómo puedo ayudarte hoy?',
      send: 'Enviar'
    },
    // Forms
    forms: {
      name: 'Nombre',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      company: 'Empresa',
      message: 'Mensaje',
      submit: 'Enviar',
      submitting: 'Enviando...',
      success: '¡Gracias! Nos pondremos en contacto pronto.',
      error: 'Algo salió mal. Por favor intenta de nuevo.'
    },
    // AI STEM Kidz Page (ES)
    aiStemKidz: {
      hero: {
        tag: 'Programa de Alfabetización en IA · 8–12 años',
        headlinePart1: 'Convierte a tu hijo de consumidor de tecnología en',
        headlinePart2: 'creador de IA.',
        subhead: 'AI STEM Kidz — programa de 6 meses basado en proyectos para edades 8–12. Construye un portafolio profesional con imágenes, videos, sitios web y apps.',
        cta: 'Únete a las Familias Fundadoras',
        trust: '20+ años de experiencia · Marco de IA centrado en el humano',
        spotsLeft: 'lugares de 25 disponibles',
        foundingNote: 'Las primeras 10 familias obtienen precio fundador de por vida.',
        playLabel: 'Haz clic para ver la intro',
        videoCaption: 'Una intro personal de Dario, tu guía de alfabetización en IA.'
      },
      problem: {
        title: 'El Problema',
        list: [
          'Los niños consumen tecnología pasivamente (scroll, juegos)',
          'Los padres no tienen guía sobre el uso seguro de IA',
          'Las escuelas van atrasadas en alfabetización en IA',
          'STEM tradicional se siente abstracto para los niños'
        ]
      },
      solution: {
        title: 'Nuestra Solución',
        list: [
          'Creación basada en proyectos con 12+ herramientas de IA',
          'IA centrada en humanos + retos "Co-Piloto" semanales para padres',
          'Currículo apropiado para edades 8–12',
          'Artefactos tangibles de portafolio + certificación cada mes',
          'Sesión semanal de tutoría FAQ'
        ]
      },
      valueProp: {
        quote: 'Transforma a tu hijo de 8–12 años de consumidor a creador de IA en 6 meses — construyendo un portafolio profesional mediante aprendizaje basado en proyectos con apoyo co-piloto de los padres.',
        highlights: ['6 meses', '12+ herramientas IA pro', 'Portafolio mensual', 'Padre co-piloto'],
        spanishNote: 'El primer y único curso AI STEM Kidz en español para niños. Los estudiantes bilingües lideran el mundo de la IA.'
      },
      how: {
        title: 'Cómo Funciona',
        subtitle: 'Cuatro pasos: de niño curioso a creador certificado de IA.',
        s1: { title: 'Inscríbete en la cohorte de 6 meses', desc: 'Las familias fundadoras reciben guía de configuración de bonus.' },
        s2: { title: 'Misiones semanales por proyecto', desc: 'Videos, sitios web, arte IA, apps simples.' },
        s3: { title: 'Retos co-piloto para padres', desc: 'Actividades de 30 min para reforzar seguridad y creatividad.' },
        s4: { title: 'Gana insignias + certificado final', desc: 'AI STEM Kidz Certified Creator tras 6 meses.' },
        cta: 'Reserva tu lugar ahora'
      },
      curriculum: {
        title: 'Herramientas de IA reales — las mismas que usan los pros ·',
        subtitle: 'Tu hijo aprende creando con IA de grado industrial, en un entorno seguro para niños.',
        sampleTitle: 'Proyectos de Ejemplo',
        projects: [
          'Crea una tira cómica con IA',
          'Diseña un avatar parlante',
          'Programa un chatbot',
          'Diseña un póster de película'
        ]
      },
      parental: {
        tag: 'Padres Incluidos',
        title: 'No te reemplazamos — te empoderamos.',
        subtitle: 'Cada semana recibes un reto co-piloto breve que convierte el aprendizaje de IA en momento familiar.',
        features: [
          'Retos co-piloto semanales (5–10 min para padres)',
          'Marco de IA centrado en humanos: ética, sesgo, verificación',
          'Sin experiencia previa — guías y soporte por email incluido',
          'Tiempo de pantalla reinventado: producción creativa, no scroll pasivo'
        ]
      },
      cert: {
        title: 'Certificación y Portafolio',
        subtitle: 'Cada mes desbloquea una insignia digital. Tras 6 meses: portafolio completo + certificado Master Creator.',
        b1: 'Explorador IA',
        b2: 'Maestro del Prompt',
        b3: 'Agente Ético IA',
        b4: 'Narrador IA',
        b5: 'Constructor de Bots',
        b6: 'Innovador IA',
        portfolio: [
          { title: 'Tira Cómica IA', desc: 'Arte de personajes y diálogo con IA segura para niños.' },
          { title: 'Video Avatar Parlante', desc: 'Clonación de voz + animación, narrado por el estudiante.' },
          { title: 'Sitio Web con Chatbot', desc: 'Un sitio funcional + chatbot desplegado por el estudiante.' }
        ],
        footnote: 'Cada mes = 1 pieza de portafolio. Tras 6 meses: portafolio digital completo para escuela o showcases.'
      },
      pricing: {
        title: 'Precio Familia Fundadora',
        subtitle: 'Dos formas de inscribirte. Ambas incluyen certificación, todas las herramientas y el manual para padres.',
        bestValue: 'Mejor Valor',
        p1Title: 'Programa de 6 meses',
        p1Note: 'Pago único',
        p1Features: [
          'Los 6 módulos · 12+ herramientas IA',
          'Insignias digitales mensuales',
          'Certificado final Master Creator',
          'Manual para padres + guías co-piloto',
          'Sesión semanal de tutoría FAQ'
        ],
        p2Title: 'Suscripción Mensual',
        p2Note: '+ 10% inscripción inicial · 10% interés por mora',
        month: 'mes',
        p2Features: [
          'Los 6 módulos · 12+ herramientas IA',
          'Insignias digitales mensuales',
          'Certificado final Master Creator',
          'Manual para padres + guías co-piloto',
          'Sesión semanal de tutoría FAQ'
        ],
        cta: 'Reservar Lugar Fundador',
        bonusTitle: 'Bonus Familias Fundadoras',
        bonusDesc: 'Inscripción GRATIS + manual gratuito',
        value: 'valor'
      },
      audience: {
        title: 'Diseñado para estas familias',
        a1: 'Padres tech-forward (35–50)',
        a2: 'Familias homeschool',
        a3: 'Padres de escuela privada',
        a4: 'Estudiantes con talento (8–12)',
        a5: 'Padres con conciencia de seguridad'
      },
      social: {
        title: 'Prueba Social y Loop Viral',
        loops: [
          'Cada showcase del estudiante compartido por padres → consultas de otros',
          'Los retos co-piloto convierten padres en embajadores',
          'Insignias digitales = prueba social en LinkedIn y currículos escolares'
        ],
        testimonial: 'Por fin, un programa de IA que me incluye como padre.',
        testimonialBy: 'Futuro Padre Fundador'
      },
      risk: {
        title: 'Preguntas frecuentes, resueltas',
        items: [
          { q: '¿Es la IA segura para mi hijo?', a: 'Sí. Usamos un marco de IA centrado en el humano con herramientas seguras para niños, co-piloto parental y un módulo de seguridad dedicado.' },
          { q: '¿Mi hijo es muy pequeño/grande (rango de edad)?', a: 'El currículo está hecho para 8–12 años, con proyectos de muestra y un kit inicial gratuito por email.' },
          { q: 'No tengo experiencia tech — ¿puedo apoyar a mi hijo?', a: 'No se necesita experiencia. Guiamos al niño y al padre con guías y soporte por email.' },
          { q: '¿Esto solo añadirá más tiempo de pantalla?', a: 'No. Enfatizamos producción creativa sobre consumo pasivo — la pantalla se vuelve herramienta, no trampa.' },
          { q: '¿Vale el precio?', a: 'Compara con tutorías: $11,999 MXN por 6 meses ≈ una pequeña inversión semanal por portafolio + certificación + fluidez en IA.' }
        ]
      },
      closing: {
        title1: '¿Tu hijo esta consumiendo o',
        title2: 'creando contenido?',
        enrol: 'Inscríbete Ahora',
        book: 'Reservar Consulta 1-1',
        disclaimer: 'Enfoque en alfabetización en IA, no reemplaza a la escuela. Lugares limitados · precio fundador de por vida.',
        founder: 'Fundador: Experto en tecnología reconocido, 20+ años.'
      },
      floating: 'Inscríbete Ahora',
      floatingShort: 'Inscríbete'
    }
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('dmtai-language');
    if (saved) return saved;
    
    // Auto-detect from browser
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('es') ? 'es' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('dmtai-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
